import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { statoAnello, testoLavoro, chiaviFinite } from './bulkAnello';
import './bulkprogress.css';

/* Progresso delle lavorazioni di massa lanciate dalla lista numeri.
   Le spie degli agenti mostrano il progresso solo quando l'agente e' acceso: lanciando un
   bulk a mano (con l'agente spento, come richiede il 456) si restava senza alcun segnale.
   Qui la lavorazione si annuncia da sola, nella riga in alto, e ti segue in tutta l'app.

   Nell'header c'e' uno spazio di larghezza fissa per sei anelli (tanti quanti gli
   strumenti), subito a sinistra del pulsante AI: la comparsa di un anello non sposta
   niente. Cliccando si apre la tendina con tutti i lavori, sopra la pagina; il clic su una
   riga apre le statistiche di quello strumento.

   Tutti e cinque i job del server espongono gli stessi tre campi — total, completed,
   status — quindi la lettura e' uniforme; cambia solo da quale endpoint arrivano. */

const LS_KEY = 'bulk-jobs';

// Da dove si legge il progresso di ogni strumento. Alcuni bulk restituiscono un job_id
// (si interroga quel job), altri accendono l'agente e il progresso sta nello stato (Indaga).
// `riassunto` dice cosa e' successo davvero: senza, una lavorazione che salta tutto
// (record gia' verificati di recente) sembrerebbe identica a una che ha lavorato tutto.
const n = (v) => (v || 0).toLocaleString('it-IT');
export const STRUMENTI = {
  '456':       { label: 'Verifica 456',      color: '#22c55e', stats: '456',
                 leggi: async (id) => (await axios.get(`${API}/jobs/${id}`)).data,
                 riassunto: (j) => [
                   j.tim_count ? `${n(j.tim_count)} TIM` : null,
                   j.non_tim_count ? `${n(j.non_tim_count)} NON TIM` : null,
                   j.errors ? `${n(j.errors)} non riuscite` : null,
                 ].filter(Boolean).join(' · ') },
  copertura:   { label: 'Copertura',         color: '#3b82f6', stats: 'copertura',
                 leggi: async (id) => (await axios.get(`${API}/copertura/job/${id}`)).data?.job,
                 riassunto: (j) => [
                   (j.completed || 0) - (j.skipped || 0) > 0 ? `${n((j.completed || 0) - (j.skipped || 0))} verificate` : null,
                   j.skipped ? `${n(j.skipped)} saltate: già verificate di recente o senza indirizzo` : null,
                 ].filter(Boolean).join(' · ') },
  ssmate:      { label: 'Lucchetto SSMATE',  color: '#eab308', stats: 'ssmate',
                 leggi: async (id) => (await axios.get(`${API}/ssmate/job/${id}`)).data,
                 riassunto: (j) => [
                   j.verificati ? `${n(j.verificati)} verificate` : null,
                   j.saltati ? `${n(j.saltati)} saltate` : null,
                   j.errori ? `${n(j.errori)} non riuscite` : null,
                 ].filter(Boolean).join(' · ') },
  vf:          { label: 'Vodafone',          color: '#e60000', stats: 'gea',
                 leggi: async (id) => (await axios.get(`${API}/gea/bulk/${id}`)).data,
                 riassunto: (j) => [
                   j.vodafone ? `${n(j.vodafone)} Vodafone` : null,
                   j.negativi ? `${n(j.negativi)} no` : null,
                   j.errori ? `${n(j.errori)} non riuscite` : null,
                 ].filter(Boolean).join(' · ') },
  indaga:      { label: 'Indaga',            color: '#f97316', stats: 'indaga',
                 // Il job espone lo standby (captcha Google) in un campo a parte
                 // (`standby`), non nello `status`: qui si traduce in status:'standby',
                 // la stessa lingua parlata da copertura e campagne, cosi' l'anello lo
                 // riconosce come "in attesa" invece di vederlo identico a "in corso".
                 leggi: async () => {
                   const j = (await axios.get(`${API}/indaga/stato`)).data?.job;
                   return j && j.standby && j.status === 'running' ? { ...j, status: 'standby' } : j;
                 },
                 riassunto: (j) => [
                   j.completati ? `${n(j.completati)} completate` : null,
                   j.non_trovati ? `${n(j.non_trovati)} non trovate` : null,
                   j.errori ? `${n(j.errori)} non riuscite` : null,
                 ].filter(Boolean).join(' · ') },
  // Nessun dialogo di statistiche per le Campagne: stats a null. L'anello di
  // progresso funziona lo stesso (leggi/riassunto), solo il click per aprire le
  // statistiche non fa nulla (nessuna voce 'campagne' nella mappa APRI_STATS in Sidebar).
  campagne:    { label: 'Da qualificare',      color: '#0ea5e9', stats: null,
                 // Il job delle campagne parla la sua lingua (attivo/totale/fatti, come il
                 // bulk dei domini): qui si traduce nei tre campi che legge l'anello,
                 // tenendo accanto i campi veri per `riassunto`. Senza, l'anello restava a
                 // zero per sempre — total/completed/status non esistevano proprio.
                 // 'standby' NON e' una fine: e' Google che chiede il captcha e il lavoro
                 // che aspetta, quindi l'anello continua a girare.
                 // Un lavoro FERMATO DA SOLO (captcha di Google, database che non risponde)
                 // chiude comunque con attivo=false: senza distinguerlo, l'anello dipingeva
                 // 100% verde con la spunta e annunciava "completata" un lavoro interrotto a
                 // 12 righe su 2.140. E' un errore, e va detto come tale.
                 // Lo stesso vale per il lavoro FERMATO A MANO (`annullato`): su un lavoro
                 // da due giorni fermarlo e' normale, ed e' l'unico ramo che il titolare
                 // usera' di sicuro.
                 leggi: async () => {
                   const d = (await axios.get(`${API}/campagne/job`)).data || {};
                   const fermo = d.fermo_per_blocco || d.fermo_per_errori || d.annullato;
                   return { ...d, total: d.totale || 0, completed: d.fatti || 0,
                            status: d.attivo ? (d.standby ? 'standby' : 'running')
                                             : (fermo ? 'error' : 'done') };
                 },
                 riassunto: (j) => [
                   // il PERCHE' si e' fermato viene per primo: e' la domanda di chi guarda
                   j.fermo_per_blocco ? 'fermo: Google chiede il captcha' : null,
                   j.fermo_per_errori ? 'fermo: errori sul database' : null,
                   j.annullato && !j.fermo_per_blocco && !j.fermo_per_errori
                     ? 'fermato a mano' : null,
                   j.risolti ? `${n(j.risolti)} numeri ricostruiti` : null,
                   (j.fatti || 0) - (j.risolti || 0) > 0
                     ? `${n((j.fatti || 0) - (j.risolti || 0))} senza esito` : null,
                 ].filter(Boolean).join(' · ') },
};

/** Riassunto leggibile di un job, senza far saltare nulla se lo strumento non ne ha uno. */
function riassuntoDi(chiave, job) {
  try { return STRUMENTI[chiave].riassunto?.(job) || ''; } catch { return ''; }
}

// ── Archivio: fuori da React, cosi' chi lancia il bulk non deve avere il componente sopra
//    di se'. Persiste in localStorage: un refresh non fa perdere di vista la lavorazione.
function carica() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}') || {}; } catch { return {}; }
}
let stato = carica();
const ascoltatori = new Set();
function scrivi(nuovo) {
  stato = nuovo;
  try { localStorage.setItem(LS_KEY, JSON.stringify(stato)); } catch { /* quota piena */ }
  ascoltatori.forEach((f) => f());
}

/** Annuncia una lavorazione appena avviata. `jobId` manca per gli strumenti che non lo danno. */
export function avviaBulk(chiave, { jobId = null, total = 0 } = {}) {
  if (!STRUMENTI[chiave]) return;
  // Il permesso si chiede qui, mentre l'utente sta avviando: e' un gesto suo, quindi il
  // browser lo accetta. Chiedere all'apertura della pagina verrebbe bloccato.
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission();
  } catch { /* notifiche non disponibili */ }
  scrivi({ ...stato, [chiave]: { chiave, jobId, total, completed: 0, status: 'running' } });
}

/** L'avviso di lavoro finito resta a schermo finche' non lo chiude l'utente: e' la
    ricevuta del lavoro fatto, non un messaggio di passaggio. */
export function chiudiBulk(chiave) {
  if (!(chiave in stato)) return;
  const n = { ...stato }; delete n[chiave]; scrivi(n);
}
const sottoscrivi = (f) => { ascoltatori.add(f); return () => ascoltatori.delete(f); };
const leggiStato = () => stato;

/** Avviso di fine lavorazione: sempre in pagina, e sul desktop solo se sei altrove.
    Stessa regola della pagina del link: se stai guardando, l'anello basta. */
function avvisaFine(chiave, job) {
  const s = STRUMENTI[chiave];
  const errore = job.status === 'error';
  const titolo = `${s.label} ${errore ? 'interrotta' : 'completata'}`;
  // Interrotta: contano le righe DAVVERO lavorate. "2.140 lanciate" dopo un'interruzione
  // alla riga 12 e' vero della coda, non del lavoro fatto, e si legge come un successo.
  const quante = errore
    ? `${n(job.completed || 0)} di ${n(job.total || 0)} lavorate`
    : `${n(job.total || job.completed || 0)} lanciate`;
  const sub = job.riassunto ? `${quante} · ${job.riassunto}` : quante;
  if (errore) toast.error(`${titolo} — ${sub}`); else toast.success(`${titolo} — ${sub}`);
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted'
        && (document.hidden || (document.hasFocus && !document.hasFocus()))) {
      const avviso = new Notification(titolo, { body: sub, icon: '/icon-192.png', tag: `bulk-${chiave}` });
      avviso.onclick = () => { try { window.focus(); avviso.close(); } catch { /* noop */ } };
    }
  } catch { /* notifiche non disponibili */ }
}

/** Chiede l'apertura della finestra statistiche di uno strumento (la monta la Sidebar). */
export const APRI_STATS = 'tim456:apri-stats';

/** Toglie in un colpo solo tutti i lavori non piu' in corso (finiti, fermi, senza esito). */
function chiudiFiniti() {
  const via = chiaviFinite(stato);
  if (!via.length) return;
  const n = { ...stato }; via.forEach((k) => delete n[k]); scrivi(n);
}

/** L'anello: pista, arco del colore dello strumento (rosso se fermo) e cosa c'e' dentro.
    Finito e fermo sono dischi pieni con spunta/esclamativo disegnati in SVG (non testo):
    stessa forma su ogni computer. La percentuale completa va nel title, il numero dentro
    l'anello non porta piu' il simbolo «%». */
function Anello({ job, strumento, grande = false }) {
  const a = statoAnello(job, strumento.color);
  const misura = grande ? 'bulkh-grande' : 'bulkh-mini';
  const titolo = a.tipo === 'corso'
    ? `${strumento.label}: ${a.perc}%${a.attesa ? ' (in attesa)' : ''}`
    : strumento.label;

  if (a.tipo === 'fine') {
    return (
      <span className={`bulkh-anello ${misura}`} style={{ '--c': a.colore }} title={titolo}>
        <svg className="bulkh-disco" viewBox="0 0 30 30" aria-hidden="true">
          <circle cx="15" cy="15" r="13.5" />
          <path d="M9.6 15.4l3.6 3.5 7.2-7.6" />
        </svg>
      </span>
    );
  }
  if (a.tipo === 'errore') {
    return (
      <span className={`bulkh-anello ${misura}`} title={titolo}>
        <svg className="bulkh-fermo" viewBox="0 0 30 30" aria-hidden="true">
          <circle cx="15" cy="15" r="13.5" />
          <line className="bulkh-asta" x1="15" y1="8.6" x2="15" y2="16.4" />
          <circle className="bulkh-punto" cx="15" cy="21.2" r="1.7" />
        </svg>
      </span>
    );
  }
  return (
    <span className={`bulkh-anello ${misura}${a.attesa ? ' bulkh-attesa' : ''}`}
      style={{ '--c': a.colore }} title={titolo}>
      <svg viewBox="0 0 30 30" aria-hidden="true">
        <circle className="bulkh-pista" cx="15" cy="15" r="12.5" />
        {a.perc > 0 && (
          <circle className="bulkh-arco" cx="15" cy="15" r="12.5" pathLength="100"
            transform="rotate(-90 15 15)" style={{ strokeDasharray: `${a.perc} 100` }} />
        )}
      </svg>
      <span className="bulkh-dentro" aria-hidden="true">{a.tipo === 'ignoto' ? '?' : a.perc}</span>
    </span>
  );
}

export default function BulkProgress() {
  const jobs = useSyncExternalStore(sottoscrivi, leggiStato, leggiStato);
  const timer = useRef(null);
  const boxRef = useRef(null);
  const [aperto, setAperto] = useState(false);
  // Un lavoro salvato da una versione vecchia con uno strumento che non esiste piu' non
  // deve far saltare l'header: si mostra solo cio' che il registro sa disegnare.
  const chiavi = Object.keys(jobs).filter((k) => STRUMENTI[k]);
  const attive = chiavi.filter((k) => jobs[k].status === 'running');
  const vuoto = chiavi.length === 0;

  // La tendina si chiude cliccando fuori o con Esc; e da sola quando non resta nessun lavoro.
  useEffect(() => {
    if (!aperto) return undefined;
    const fuori = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setAperto(false); };
    const esc = (e) => { if (e.key === 'Escape') setAperto(false); };
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', fuori); document.removeEventListener('keydown', esc); };
  }, [aperto]);
  useEffect(() => { if (vuoto) setAperto(false); }, [vuoto]);

  // Un solo battito per tutte le lavorazioni attive: interroga ognuna e aggiorna i contatori.
  useEffect(() => {
    if (!attive.length) { clearInterval(timer.current); return undefined; }
    const battito = async () => {
      const agg = { ...stato };
      let cambiato = false;
      for (const k of Object.keys(agg)) {
        if (agg[k].status !== 'running') continue;
        let j = null;
        try { j = await STRUMENTI[k].leggi(agg[k].jobId); } catch { j = null; }
        // Copertura e Indaga espongono il job SOLO finche' e' vivo (_job_corrente filtra su
        // running/standby): appena finisce lo stato risponde `job: null`. Senza questo ramo
        // l'anello restava fermo all'ultimo conteggio letto — 2 su 4 per sempre —
        // mentre il lavoro era gia' concluso.
        const nostro = j && (!agg[k].jobId || !j.id || j.id === agg[k].jobId);
        if (!nostro) {
          const vuoti = (agg[k].vuoti || 0) + 1;
          // Mai visto vivo: aspetta qualche battito (puo' nascere con un attimo di ritardo),
          // poi chiudi comunque. Durante il lavoro non c'e' la X, quindi un anello che
          // non si chiude da solo resterebbe li' per sempre.
          if (agg[k].visto) {
            agg[k] = { ...agg[k], vuoti, status: 'done', completed: agg[k].total || agg[k].completed };
            if (stato[k] && stato[k].status === 'running') avvisaFine(k, agg[k]);
          } else if (vuoti >= 12) {
            agg[k] = { ...agg[k], vuoti, status: 'done', ignoto: true };
          } else {
            agg[k] = { ...agg[k], vuoti };
          }
          cambiato = true;
          continue;
        }
        const fine = j.status && j.status !== 'running' && j.status !== 'standby';
        agg[k] = { ...agg[k], visto: true, vuoti: 0,
                   total: j.total ?? agg[k].total, completed: j.completed ?? agg[k].completed,
                   riassunto: riassuntoDi(k, j) || agg[k].riassunto || '',
                   // Lo standby resta distinto da 'running': serve all'anello per disegnare
                   // la pista tratteggiata "in attesa". Non e' una fine (vedi `fine` sopra).
                   status: fine ? (j.status === 'error' ? 'error' : 'done') : (j.status === 'standby' ? 'standby' : 'running') };
        // `agg` e' una copia fatta a inizio battito: se due battiti si accavallano
        // potrebbero annunciare due volte la stessa fine. Decide lo stato VIVO.
        if (fine && stato[k] && stato[k].status === 'running') avvisaFine(k, agg[k]);
        cambiato = true;
      }
      if (cambiato) scrivi(agg);
    };
    battito();
    timer.current = setInterval(battito, 2500);
    return () => clearInterval(timer.current);
  }, [attive.length]); // eslint-disable-line

  const apriStats = (s) => {
    if (!s.stats) return;
    setAperto(false);
    window.dispatchEvent(new CustomEvent(APRI_STATS, { detail: s.stats }));
  };
  const finiti = chiavi.filter((k) => statoAnello(jobs[k], '').chiudibile);

  // Lo spazio c'e' SEMPRE, anche vuoto: e' largo quanto sei anelli, cosi' il primo che
  // compare non sposta pulsante AI, ora e pallino. La tendina e' in posizione assoluta.
  return (
    <div ref={boxRef} className="bulkh">
      <button type="button" className="bulkh-slot" disabled={vuoto}
        aria-haspopup="true" aria-expanded={aperto}
        aria-label={vuoto ? 'Nessun lavoro in corso' : `Lavori: ${chiavi.length}`}
        onClick={() => setAperto((o) => !o)}>
        {chiavi.map((k) => <Anello key={k} job={jobs[k]} strumento={STRUMENTI[k]} />)}
      </button>

      {aperto && !vuoto && (
        <div className="bulkh-pannello" role="dialog" aria-label="Lavori">
          <div className="bulkh-testa">
            <b>Lavori</b>
            <button type="button" className="bulkh-azione" disabled={!finiti.length}
              onClick={chiudiFiniti}>Chiudi i finiti</button>
          </div>
          {chiavi.map((k) => {
            const j = jobs[k]; const s = STRUMENTI[k];
            const a = statoAnello(j, s.color);
            const t = testoLavoro(j);
            return (
              <div key={k} className={`bulkh-lavoro${a.chiudibile ? ' bulkh-chiudibile' : ''}`}
                style={{ '--c': a.colore }}>
                {/* Senza finestra di statistiche (Campagne) la riga non promette un clic
                    che non fa niente: niente `title` e il clic non parte proprio. */}
                <button type="button" className={`bulkh-riga${s.stats ? '' : ' bulkh-senza-stats'}`}
                  title={s.stats ? `${s.label}: apri le statistiche` : undefined}
                  onClick={() => apriStats(s)}>
                  <Anello job={j} strumento={s} grande />
                  <span className="bulkh-testo">
                    <span className="bulkh-lbl">{s.label}</span>
                    <span className="bulkh-num">
                      {t.esito && (
                        <span className={a.tipo === 'errore' ? 'bulkh-fermo' : 'bulkh-fatto'}>{t.esito}</span>
                      )}
                      {t.esito && t.dettaglio ? ' · ' : ''}
                      {t.dettaglio}
                    </span>
                  </span>
                </button>
                {/* La X esiste solo a lavoro finito o fermo: durante non c'e' niente da chiudere. */}
                {a.chiudibile && (
                  <button type="button" className="bulkh-x" title="Chiudi" aria-label={`Chiudi ${s.label}`}
                    onClick={() => chiudiBulk(k)}>×</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

